import React, { useState } from "react";
import { Building2, MapPin } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getBranches,
  getIsBranchModalOpen,
  setSelectedBranch,
} from "../../features/settings/settingsSlice";
import { BranchType } from "../../types/branch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { t } from "../../locales";

const BranchModal = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(getIsBranchModalOpen);
  const branches = useAppSelector(getBranches);

  const [selectedId, setSelectedId] = useState<string>("");

  const selectedBranch: BranchType | undefined = branches.find(
    (b) => String(b.id) === selectedId
  );

  const handleConfirm = () => {
    if (selectedBranch) {
      dispatch(setSelectedBranch(selectedBranch));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        hideClose
        className="w-full max-w-lg mx-4 p-10"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#113F67]/10 flex-shrink-0">
              <Building2 className="w-8 h-8 text-[#113F67]" />
            </div>
            <div>
              <DialogTitle className="text-[#113F67] font-bold text-center">{t.branch.modalTitle}</DialogTitle>
              <DialogDescription className="text-center">
                {t.branch.modalDescription}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="h-12 border-2 border-gray-200 rounded-xl outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand-dark focus:shadow-focus-brand">
                <SelectValue placeholder={t.branch.selectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={String(branch.id)}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span>
                        {branch.name}{" "}
                        <span className="text-gray-400">({branch.code})</span>
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedBranch?.address && (
            <div className="flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-600">
              <MapPin className="mt-0.5 w-4 h-4 flex-shrink-0 text-gray-400" />
              <span>{selectedBranch.address}</span>
            </div>
          )}

          <button
            onClick={handleConfirm}
            disabled={!selectedId}
            className="w-full h-12 px-4 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b]"
          >
            {t.branch.confirmButton}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BranchModal;
